import { Typography, Button, Box, Tooltip } from "@mui/material";
import { ReactNode, useState } from "react";
import { BLOCKED_IMAGE_URL } from "#next/utils/shared";
import { trpc } from "#next/utils/trpc";
import { ConfirmDialog } from "#next/components/reusable/ConfirmDialog";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import theme from "#next/theme";
import { useUser } from "@auth0/nextjs-auth0/client";
import { SearchResult } from "#next/utils/interfaces";

export function getProxiedMediaUrl(post: SearchResult): string {
  if (post.mediaIsBlocked) {
    return BLOCKED_IMAGE_URL;
  } else {
    return `/api/images/${post.platform}/${post.id}`;
  }
}

const Video = ({
  mediaUrl,
  setMediaSrcHadError,
}: {
  mediaUrl: string;
  setMediaSrcHadError: (hadError: boolean) => void;
}) => {
  return (
    <video
      width="100%"
      height="200"
      onError={() => {
        setMediaSrcHadError(true);
      }}
      controls
    >
      <source src={mediaUrl} type="video/mp4" />
    </video>
  );
};

const IFramePlayer = ({ mediaUrl }: { mediaUrl: string }) => {
  return (
    <iframe
      style={{ width: "100%", height: 400 }}
      src={mediaUrl}
      allowFullScreen
    />
  );
};

const Image = ({
  post,
  setMediaSrcHadError,
  blurMedia,
}: {
  post: SearchResult;
  setMediaSrcHadError: (hadError: boolean) => void;
  blurMedia?: boolean;
}) => {
  return (
    // eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text
    <img
      style={{
        width: "100%",
        filter: blurMedia ? "blur(10px)" : "none",
      }}
      src={getProxiedMediaUrl(post)}
      onError={() => {
        setMediaSrcHadError(true);
      }}
    />
  );
};

export const ReportableMedia = ({
  post,
  handleMediaClicked,
  onMediaReported,
  blurMedia,
}: {
  post: SearchResult;
  handleMediaClicked: () => void;
  onMediaReported: () => void;
  blurMedia?: boolean;
}) => {
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [mediaSrcHadError, setMediaSrcHadError] = useState(false);

  const mutation = trpc.reportedPosts.reportPost.useMutation();
  const utils = trpc.useContext();
  const { user } = useUser();

  const { id, mediaIsBlocked, media } = post;
  const mediaUrl = media[0];

  if (!mediaUrl) return null;

  const handleReportMediaClick = () => {
    mutation.mutate(
      { postId: id, contentType: "CSAM" },
      {
        onSuccess: () => {
          // Clear all client-side post data and refetch to make sure the reported media is blocked
          utils.savedSearch.runSavedSearch.invalidate();
          utils.userSearch.runUserPostsSearch.invalidate();
          setDialogIsOpen(false);
        },
      },
    );
    onMediaReported();
  };

  const dialogContentText = (
    <>
      <Typography sx={{ mb: 1 }}>
        If you believe this media may depict sexual abuse of a child, then
        please click &quot;Report Media&quot;.
      </Typography>
      <Typography sx={{ mb: 1 }}>
        The media will be blocked for all users of the application, pending
        review.
      </Typography>
      <Typography sx={{ mb: 1 }}>
        If review determines that the media may depict abuse then Pyrra will
        report it to the relevant authorities, and assist them if requested.
      </Typography>
    </>
  );

  const dialogCancelButton = (
    <Button onClick={() => setDialogIsOpen(false)}>Cancel</Button>
  );

  const dialogConfirmButton = (
    <Button onClick={handleReportMediaClick}>Report Media</Button>
  );

  if (mediaSrcHadError) {
    return null;
  }

  let mediaComponent: ReactNode;
  if (mediaIsBlocked) {
    mediaComponent = (
      <img
        style={{
          maxWidth: "200px",
          maxHeight: "100px",
        }}
        src={BLOCKED_IMAGE_URL}
        alt="This media has been reported and is currently blocked, pending review"
      />
    );
  } else {
    // TODO: We have the content type in the Elasticsearch data, maybe we should use it
    const videoExtensions = [".mp4", ".mov", ".wmv", ".avi"];
    if (videoExtensions.some((v) => mediaUrl.includes(v))) {
      mediaComponent = (
        <Video mediaUrl={mediaUrl} setMediaSrcHadError={setMediaSrcHadError} />
      );
    } else if (mediaUrl.includes("/embed/")) {
      mediaComponent = <IFramePlayer mediaUrl={mediaUrl} />;
    } else {
      mediaComponent = (
        <Image
          blurMedia={blurMedia}
          post={post}
          setMediaSrcHadError={setMediaSrcHadError}
        />
      );
    }
  }

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        onClick={handleMediaClicked}
        sx={{
          cursor: "pointer",
          float: "right",
          width: "100%",
        }}
      >
        {mediaComponent}
      </Box>
      {!mediaIsBlocked && user && (
        <Tooltip title="Report this media" placement="top">
          <ReportProblemIcon
            sx={{
              backgroundColor: "black",
              position: "absolute",
              top: 0,
              right: 0,
              padding: "2px",
              borderRadius: "3px",
              "&:hover": {
                color: theme.palette.warning.light,
                opacity: "100%",
              },
              opacity: "65%",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setDialogIsOpen(true);
            }}
          />
        </Tooltip>
      )}
      <ConfirmDialog
        isOpen={dialogIsOpen}
        onClose={() => setDialogIsOpen(false)}
        title="Report Child Sexual Abuse Media"
        contentText={dialogContentText}
        confirmButton={dialogConfirmButton}
        cancelButton={dialogCancelButton}
      />
    </Box>
  );
};
